import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import axios from 'axios';
import DOMPurify from 'dompurify';

// Lijst met ongewenste woorden
const BLOCKED_WORDS = [
  'spam', 'reclame', 'advertentie', 'casino', 'viagra',
  'lottery', 'prize', 'winner', 'http://', 'https://',
  'script', 'iframe', 'onload', 'onerror', 'javascript:',
  'data:', 'vbscript:', 'expression(', 'eval('
];

// Maximum aantal pogingen
const MAX_ATTEMPTS = 3;
const ATTEMPT_TIMEOUT = 15 * 60 * 1000; // 15 minuten
const DEBOUNCE_DELAY = 500; // 500ms debounce voor input validatie

function App() {
  const [formData, setFormData] = useState({ email: '', reactie: '' });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [attempts, setAttempts] = useState(0);
  const [lastAttemptTime, setLastAttemptTime] = useState(0);
  const [csrfToken, setCsrfToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationTimeout, setValidationTimeout] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Haal CSRF token op bij het laden van de pagina
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await axios.get('/api/csrf-token', {
          withCredentials: true // Belangrijk voor CSRF cookies
        });
        setCsrfToken(response.data.csrfToken);
      } catch (error) {
        console.error('Fout bij ophalen CSRF token:', error);
        setSnackbar({
          open: true,
          message: 'Er is een fout opgetreden bij het laden van het formulier. Ververs de pagina.',
          severity: 'error'
        });
      }
    };
    fetchCsrfToken();
  }, []);

  // E-mail validatie functie
  const validateEmail = useCallback((email) => {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    
    if (!email) {
      return 'E-mailadres is verplicht';
    }
    if (email.length < 5) {
      return 'E-mailadres is te kort';
    }
    if (email.length > 254) {
      return 'E-mailadres is te lang';
    }
    if (!email.includes('.')) {
      return 'E-mailadres moet een geldig domein bevatten';
    }
    if (!emailRegex.test(email)) {
      return 'Voer een geldig e-mailadres in';
    }
    // Extra check voor verdachte karakters
    if (/[<>{}[\]\\]/.test(email)) {
      return 'E-mailadres bevat ongeldige tekens';
    }
    return '';
  }, []);

  // Tekst validatie functie
  const validateReactie = useCallback((text) => {
    if (!text) {
      return 'Vul een bericht in';
    }
    if (text.length < 10) {
      return 'Uw bericht moet minimaal 10 tekens bevatten';
    }
    if (text.length > 1000) {
      return 'Uw bericht mag maximaal 1000 tekens bevatten';
    }

    // Controleer op herhaalde tekens
    const repeatedCharsRegex = /(.)\1{4,}/;
    if (repeatedCharsRegex.test(text)) {
      return 'Uw bericht bevat te veel herhaalde tekens';
    }

    // Controleer op herhaalde woorden
    const words = text.toLowerCase().split(/\s+/);
    const wordCount = words.reduce((acc, word) => {
      acc[word] = (acc[word] || 0) + 1;
      return acc;
    }, {});

    for (const [word, count] of Object.entries(wordCount)) {
      if (word.length > 3 && count > 5) {
        return 'Uw bericht bevat te veel herhalingen van hetzelfde woord';
      }
    }

    // Controleer op ongewenste woorden
    const lowerText = text.toLowerCase();
    for (const word of BLOCKED_WORDS) {
      if (lowerText.includes(word)) {
        return 'Uw bericht bevat ongewenste inhoud';
      }
    }

    // Controleer op te veel hoofdletters
    const upperCaseCount = (text.match(/[A-Z]/g) || []).length;
    if (upperCaseCount > text.length * 0.5 && text.length > 20) {
      return 'Uw bericht bevat te veel hoofdletters';
    }

    // Controleer op verdachte HTML/script tags
    if (/<[^>]*>/.test(text)) {
      return 'Uw bericht bevat ongeldige tekens';
    }

    // Controleer op verdachte URL's
    if (/https?:\/\/|www\./i.test(text)) {
      return 'URLs zijn niet toegestaan in het bericht';
    }

    return '';
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Sanitize input
    const sanitizedValue = DOMPurify.sanitize(value, {
      ALLOWED_TAGS: [], // Geen HTML tags toestaan
      ALLOWED_ATTR: [] // Geen attributen toestaan
    });

    setFormData(prev => ({ ...prev, [name]: sanitizedValue }));

    // Debounced validatie
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }

    const timeout = setTimeout(() => {
      const error = name === 'email' ? validateEmail(sanitizedValue) : validateReactie(sanitizedValue);
      if (error) {
        setErrors(prev => ({ ...prev, [name]: error }));
      } else {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    }, DEBOUNCE_DELAY);

    setValidationTimeout(timeout);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) {
      return;
    }

    // Controleer aantal pogingen
    const now = Date.now();
    if (attempts >= MAX_ATTEMPTS && now - lastAttemptTime < ATTEMPT_TIMEOUT) {
      setSnackbar({
        open: true,
        message: `Te veel pogingen. Probeer het over ${Math.ceil((ATTEMPT_TIMEOUT - (now - lastAttemptTime)) / 60000)} minuten opnieuw.`,
        severity: 'error'
      });
      return;
    }
    
    // Validatie
    const emailError = validateEmail(formData.email);
    const reactieError = validateReactie(formData.reactie);
    
    const newErrors = {};
    if (emailError) newErrors.email = emailError;
    if (reactieError) newErrors.reactie = reactieError;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setAttempts(prev => prev + 1);
      setLastAttemptTime(now);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post('/api/contact', formData, {
        headers: {
          'X-CSRF-Token': csrfToken,
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });
      
      // Toon succesmelding
      setSnackbar({
        open: true,
        message: 'Uw bericht is succesvol verzonden.',
        severity: 'success'
      });
      
      // Reset formulier en pogingen
      setFormData({ email: '', reactie: '' });
      setErrors({});
      setAttempts(0);
      
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Er is een fout opgetreden bij het verzenden van uw bericht.';
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      
      if (error.response?.data?.errors) {
        const serverErrors = {};
        error.response.data.errors.forEach(err => {
          serverErrors[err.field] = err.message;
        });
        setErrors(serverErrors);
      }
      
      setAttempts(prev => prev + 1);
      setLastAttemptTime(now);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Box sx={{ 
          textAlign: 'center', 
          mb: 4,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}>
          <img
            src="/vidax.jpg"
            alt="Vidax Logo"
            style={{
              maxWidth: '60%',
              height: 'auto',
              maxHeight: '50px'
            }}
          />
          <img
            src="/image.webp"
            alt="QR Code"
            style={{
              maxWidth: '40%',
              height: 'auto',
              maxHeight: '120px'
            }}
          />
        </Box>

        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          component="h1" 
          gutterBottom 
          align="center"
          sx={{ mb: 4 }}
        >
          Contactformulier
        </Typography>

        <Box 
          component="form" 
          onSubmit={handleSubmit}
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}
        >
          <TextField
            fullWidth
            label="E-mailadres"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            required
            margin="normal"
            variant="outlined"
            inputProps={{
              maxLength: 254,
              pattern: "[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*"
            }}
          />

          <TextField
            fullWidth
            label="Reactie"
            name="reactie"
            multiline
            rows={isMobile ? 4 : 6}
            value={formData.reactie}
            onChange={handleChange}
            error={!!errors.reactie}
            helperText={errors.reactie}
            required
            margin="normal"
            variant="outlined"
            inputProps={{
              maxLength: 1000
            }}
          />

          <Box sx={{ 
            textAlign: 'center',
            mt: 2,
            width: '100%'
          }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size={isMobile ? "medium" : "large"}
              endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              fullWidth
              disabled={isSubmitting}
              sx={{
                py: 1.5
              }}
            >
              {isSubmitting ? 'Verzenden...' : 'Versturen'}
            </Button>
          </Box>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
}

export default App; 